/**
 * Bringing the old workbook in.
 *
 * The screen is deliberately a *review* screen rather than an upload button.
 * Choosing a file is the smallest part of this; the part that matters is the
 * stretch between "CenoX has read your spreadsheet" and "CenoX has written to
 * your books", and everything here exists to make that stretch legible:
 *
 *   counts first — what will happen, in four numbers
 *   mapping     — what CenoX thinks each column means, editable
 *   failures    — every row it cannot take, with the reason and the row number
 *   commit      — a deliberate, separate act
 *
 * Nothing on this page writes a business record until Import is pressed.
 */

import { useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileSpreadsheet, RotateCcw, Upload } from 'lucide-react';
import {
  IMPORT_DATASETS,
  IMPORT_DATASET_LABELS,
  IMPORT_STATUS_LABELS,
  type ImportBatchDetail,
  type ImportBatchSummary,
  type ImportDataset,
  type ImportRowView,
} from '@cenox/shared';
import { useAuth } from '../../lib/auth';
import { useRecordLocation } from '../../lib/locationContext';
import { ApiError, api } from '../../lib/api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Modal,
  Select,
  Spinner,
} from '../../components/ui';

export function ImportPage(): ReactNode {
  const { business, can } = useAuth();
  const queryClient = useQueryClient();
  const path = (suffix: string) => `/businesses/${business?.businessId}${suffix}`;

  const [dataset, setDataset] = useState<ImportDataset>('sales');
  /**
   * Where the imported records belong.
   *
   * An import writes hundreds of operational rows at once, so filing them at a
   * silent default is the same mistake as a sale with no location, multiplied.
   * The file may name a location per row; when it does not, this is the answer,
   * and the API refuses the rows rather than guessing.
   */
  const destination = useRecordLocation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rollbackFor, setRollbackFor] = useState<ImportBatchSummary | null>(null);

  const history = useQuery({
    queryKey: ['imports', business?.businessId],
    queryFn: () => api.get<{ data: ImportBatchSummary[] }>(path('/imports')),
    enabled: Boolean(business),
  });

  const batch = useQuery({
    queryKey: ['import', business?.businessId, activeId],
    queryFn: () => api.get<ImportBatchDetail>(path(`/imports/${activeId}`)),
    enabled: Boolean(business && activeId),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['imports'] });
    void queryClient.invalidateQueries({ queryKey: ['import'] });
  };

  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      // The field must precede the file: the server reads `dataset` off the
      // same part stream, and a field that arrives after the file has already
      // been consumed is a field the handler never sees.
      form.append('dataset', dataset);
      form.append('file', file);
      return api.upload<ImportBatchDetail>(path('/imports'), form);
    },
    onSuccess: (result) => {
      setActiveId(result.id);
      setError(null);
      refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'That file could not be read.'),
  });

  const commit = useMutation({
    mutationFn: (id: string) =>
      api.post<ImportBatchDetail>(path(`/imports/${id}/commit`), {
        destinationBranchId: destination.branchId || null,
      }),
    onSuccess: () => {
      setError(null);
      refresh();
      // Everything an import touches. A migration that leaves the dashboard
      // showing pre-import figures invites the owner to import again.
      for (const key of ['dashboard', 'reports', 'sales', 'catalogue', 'customers', 'shop-context']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'The import could not be completed.'),
  });

  const rollback = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<ImportBatchDetail>(path(`/imports/${id}/rollback`), { reason }),
    onSuccess: () => {
      setRollbackFor(null);
      refresh();
      for (const key of ['dashboard', 'reports', 'sales', 'catalogue', 'customers', 'shop-context']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'The rollback did not complete.'),
  });

  const saveMapping = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.post(path(`/imports/${id}/save-mapping`), { name }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['import-mappings'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Could not save that mapping.'),
  });

  const remap = useMutation({
    mutationFn: ({ id, mapping }: { id: string; mapping: Record<string, string> }) =>
      api.patch<ImportBatchDetail>(path(`/imports/${id}/mapping`), { mapping }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['import'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'That mapping could not be applied.'),
  });

  if (!can('imports:manage')) {
    return (
      <EmptyState
        title="You do not have access to imports"
        description="Importing rewrites financial history in bulk, so it is limited to owners and admins."
      />
    );
  }

  const onFile = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) upload.mutate(file);
    event.target.value = '';
  };

  const detail = batch.data;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Import from Excel</h1>
        <p className="text-sm text-slate-500">
          Bring your existing records into CenoX. Nothing is saved until you review and confirm.
        </p>
      </header>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <Card>
        <CardHeader
          title="Upload a workbook"
          subtitle="Excel (.xlsx) or CSV. The first sheet with data is used."
        />
        <div className="flex flex-wrap items-end gap-4 p-4 pt-0">
          <Field label="What does this file contain?">
            {(id) => (
              <Select
                id={id}
                value={dataset}
                onChange={(e) => setDataset(e.target.value as ImportDataset)}
                options={IMPORT_DATASETS.map((d) => ({ value: d, label: IMPORT_DATASET_LABELS[d] }))}
              />
            )}
          </Field>

          <label className="inline-flex">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="sr-only"
              onChange={onFile}
              disabled={upload.isPending}
            />
            <span
              className={`inline-flex cursor-pointer items-center gap-2 rounded-lg bg-indigo-700 px-4 py-2 text-sm font-medium text-white ${
                upload.isPending ? 'opacity-60' : 'hover:bg-indigo-800'
              }`}
            >
              <Upload className="h-4 w-4" />
              {upload.isPending ? 'Reading…' : 'Choose file'}
            </span>
          </label>

          {dataset === 'products' && (
            <p className="text-xs text-slate-500">
              A quantity column becomes each item&rsquo;s opening stock balance.
            </p>
          )}
        </div>
      </Card>

      {batch.isLoading && activeId && <Spinner label="Reading your workbook" />}

      {detail && (
        <BatchReview
          detail={detail}
          destination={destination}
          onCommit={() => commit.mutate(detail.id)}
          committing={commit.isPending}
          onRemap={(mapping) => remap.mutate({ id: detail.id, mapping })}
          remapping={remap.isPending}
          onSaveMapping={(name) => saveMapping.mutate({ id: detail.id, name })}
          savingMapping={saveMapping.isPending}
          mappingSaved={saveMapping.isSuccess}
        />
      )}

      <Card>
        <CardHeader title="Previous imports" />
        {history.isLoading ? (
          <div className="p-4">
            <Spinner />
          </div>
        ) : !history.data?.data.length ? (
          <div className="p-4 text-sm text-slate-500">Nothing imported yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-2">File</th>
                  <th className="px-4 py-2">Contains</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Imported</th>
                  <th className="px-4 py-2 text-right">Duplicates</th>
                  <th className="px-4 py-2 text-right">Failed</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {history.data.data.map((b) => (
                  <tr key={b.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 font-medium text-indigo-700 hover:underline"
                        onClick={() => setActiveId(b.id)}
                      >
                        <FileSpreadsheet className="h-4 w-4" />
                        {b.filename}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-slate-600">
                      {IMPORT_DATASET_LABELS[b.dataset]}
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone={toneFor(b.status)}>{IMPORT_STATUS_LABELS[b.status]}</Badge>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{b.importedCount}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-500">
                      {b.duplicateCount}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {b.failedCount > 0 ? (
                        <span className="text-red-700">{b.failedCount}</span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {b.status === 'committed' && (
                        <Button variant="ghost" onClick={() => setRollbackFor(b)}>
                          <RotateCcw className="mr-1 h-3.5 w-3.5" />
                          Undo
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={Boolean(rollbackFor)}
        onClose={() => setRollbackFor(null)}
        title="Undo this import"
      >
        {rollbackFor && (
          <RollbackForm
            batch={rollbackFor}
            pending={rollback.isPending}
            onCancel={() => setRollbackFor(null)}
            onConfirm={(reason) => rollback.mutate({ id: rollbackFor.id, reason })}
          />
        )}
      </Modal>
    </div>
  );
}

function toneFor(status: ImportBatchSummary['status']): 'success' | 'warning' | 'neutral' {
  if (status === 'committed') return 'success';
  if (status === 'preview') return 'warning';
  return 'neutral';
}

// ── Review ──────────────────────────────────────────────────────────────────

function BatchReview({
  detail,
  destination,
  onCommit,
  committing,
  onRemap,
  remapping,
  onSaveMapping,
  savingMapping,
  mappingSaved,
}: {
  detail: ImportBatchDetail;
  destination: ReturnType<typeof useRecordLocation>;
  onCommit: () => void;
  committing: boolean;
  onRemap: (mapping: Record<string, string>) => void;
  remapping: boolean;
  onSaveMapping: (name: string) => void;
  savingMapping: boolean;
  mappingSaved: boolean;
}): ReactNode {
  const [mapping, setMapping] = useState<Record<string, string>>(detail.columnMapping);
  const [mappingName, setMappingName] = useState('');
  const committed = detail.status !== 'preview';

  /**
   * Which datasets land somewhere, and whether this file says where itself.
   *
   * A customers sheet is master data — a customer is the business's customer,
   * not one shop's — so it needs no destination. Sales, stock and opening
   * balances do, unless the workbook has a location column mapped, in which
   * case each row states its own and a mixed-location file imports correctly.
   */
  const needsDestination = detail.dataset !== 'customers';
  const fileNamesLocation = Boolean(mapping['branchName']);
  const destinationMissing = needsDestination && !fileNamesLocation && !destination.branchId;

  // A fresh batch replaces whatever the previous one was being edited as.
  const key = detail.id + detail.totalRows;
  const [seenKey, setSeenKey] = useState(key);
  if (seenKey !== key) {
    setSeenKey(key);
    setMapping(detail.columnMapping);
  }

  const dirty = useMemo(
    () => JSON.stringify(mapping) !== JSON.stringify(detail.columnMapping),
    [mapping, detail.columnMapping],
  );

  const blocked = detail.missingRequired.length > 0;

  return (
    <Card>
      <CardHeader
        title={detail.filename}
        subtitle={`${IMPORT_DATASET_LABELS[detail.dataset]} · ${detail.totalRows} rows read${
          detail.sheetName ? ` from "${detail.sheetName}"` : ''
        }`}
      />

      <div className="grid grid-cols-2 gap-3 p-4 pt-0 sm:grid-cols-4">
        <Count
          label={committed ? 'Imported' : 'Will import'}
          value={committed ? detail.importedCount : detail.readyCount}
          tone="good"
        />
        <Count label="Already in CenoX" value={detail.duplicateCount} tone="muted" />
        <Count label="Skipped" value={detail.skippedCount} tone="muted" />
        <Count label="Failed" value={detail.failedCount} tone={detail.failedCount ? 'bad' : 'muted'} />
      </div>

      {!committed && (
        <div className="border-t border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-900">Column mapping</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            CenoX matched these automatically. Correct anything it read wrongly — your file is not
            re-uploaded.
          </p>

          {blocked && (
            <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Map every required column before importing.
            </p>
          )}

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {detail.fields.map((field) => (
              <Field
                key={field.field}
                label={field.label}
                required={field.required}
                hint={field.hint ?? undefined}
                error={
                  detail.missingRequired.includes(field.field) ? 'This column is required' : undefined
                }
              >
                {(id) => (
                  <Select
                    id={id}
                    value={mapping[field.field] ?? ''}
                    onChange={(e) => {
                      const next = { ...mapping };
                      if (e.target.value) next[field.field] = e.target.value;
                      else delete next[field.field];
                      setMapping(next);
                    }}
                    options={[
                      { value: '', label: '— not in this file —' },
                      ...detail.headers.map((h) => ({ value: h, label: h })),
                    ]}
                  />
                )}
              </Field>
            ))}
          </div>

          {detail.unmappedHeaders.length > 0 && (
            <p className="mt-3 text-xs text-slate-500">
              Not used: {detail.unmappedHeaders.join(', ')}
            </p>
          )}

          {needsDestination && !committed && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
              {fileNamesLocation ? (
                <p className="text-xs text-slate-600">
                  This file has a location column, so each row is filed where it says. A row that
                  names a location CenoX does not have is reported rather than imported.
                </p>
              ) : destination.locations.length === 0 ? (
                <p className="text-xs text-amber-900">
                  You have no location to import into. Ask an owner to assign you one under Shop →
                  Locations.
                </p>
              ) : (
                <label className="text-xs text-slate-600">
                  Import these records into
                  <select
                    value={destination.branchId}
                    onChange={(e) => destination.setBranchId(e.target.value)}
                    aria-label="Import these records into"
                    className="mt-1 block rounded-lg border border-slate-300 px-2 py-2 text-sm"
                  >
                    <option value="">Choose a location</option>
                    {destination.locations.map((place) => (
                      <option key={place.id} value={place.id}>
                        {place.name}
                      </option>
                    ))}
                  </select>
                  <span className="mt-1 block text-slate-500">
                    Every row in this file is filed here. Add a location column to the workbook to
                    split one import across locations.
                  </span>
                </label>
              )}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button variant="secondary" disabled={!dirty || remapping} onClick={() => onRemap(mapping)}>
              {remapping ? 'Re-checking…' : 'Apply mapping'}
            </Button>
            <Button
              disabled={
                committing || blocked || dirty || detail.readyCount === 0 || destinationMissing
              }
              onClick={onCommit}
            >
              {committing
                ? 'Importing…'
                : `Import ${detail.readyCount} row${detail.readyCount === 1 ? '' : 's'}`}
            </Button>
            {dirty && (
              <span className="text-xs text-slate-500">Apply the mapping to see updated counts.</span>
            )}
          </div>

          {/*
            Saving is offered rather than assumed. The built-in column guesses
            are ordinary business vocabulary and will be wrong for somebody;
            saving is how an organization teaches CenoX its own words once,
            instead of correcting the same sheet every month. Matched by header
            shape, so next month's export of the same report needs no screen.
          */}
          {!dirty && !blocked && (
            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
              {mappingSaved ? (
                <p className="text-xs text-emerald-800">
                  Saved. The next sheet with these columns will be mapped this way automatically.
                </p>
              ) : (
                <>
                  <p className="text-xs text-slate-600">
                    Reuse this mapping for future uploads of the same sheet?
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <input
                      className="min-w-56 flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
                      placeholder="Name it, e.g. Monthly sales from the till book"
                      value={mappingName}
                      onChange={(e) => setMappingName(e.target.value)}
                    />
                    <Button
                      variant="secondary"
                      disabled={mappingName.trim().length < 1 || savingMapping}
                      onClick={() => onSaveMapping(mappingName.trim())}
                    >
                      {savingMapping ? 'Saving…' : 'Save mapping'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {committed && (
        <div className="flex items-center gap-2 border-t border-slate-200 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4" />
          {detail.status === 'rolled_back'
            ? 'This import was undone. The records it created have been reversed.'
            : 'Imported. These records now appear in Sales, Products and your reports.'}
        </div>
      )}

      {detail.failures.length > 0 && <Failures rows={detail.failures} />}

      {!committed && detail.rows.length > 0 && <Sample detail={detail} />}
    </Card>
  );
}

function Count({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'good' | 'bad' | 'muted';
}): ReactNode {
  const colour =
    tone === 'good' ? 'text-emerald-700' : tone === 'bad' ? 'text-red-700' : 'text-slate-500';
  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-xl font-semibold tabular-nums ${colour}`}>{value}</div>
    </div>
  );
}

/**
 * Every row that could not be taken, with its workbook row number.
 *
 * Uncapped and never collapsed into "23 rows failed". The owner has to be able
 * to open the spreadsheet beside this list and fix row 412 specifically.
 */
function Failures({ rows }: { rows: ImportRowView[] }): ReactNode {
  return (
    <div className="border-t border-slate-200 p-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-red-800">
        <AlertTriangle className="h-4 w-4" />
        Rows CenoX could not import ({rows.length})
      </h3>
      <div className="mt-3 max-h-80 overflow-y-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Row</th>
              <th className="px-3 py-2">Reason</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-slate-100">
                <td className="px-3 py-2 align-top tabular-nums text-slate-500">{row.rowNumber}</td>
                <td className="px-3 py-2 text-slate-800">{row.errorMessage}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Sample({ detail }: { detail: ImportBatchDetail }): ReactNode {
  return (
    <div className="border-t border-slate-200 p-4">
      <h3 className="text-sm font-semibold text-slate-900">First rows, as CenoX read them</h3>
      <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Row</th>
              <th className="px-3 py-2">Status</th>
              {detail.fields
                .filter((f) => detail.columnMapping[f.field])
                .map((f) => (
                  <th key={f.field} className="px-3 py-2">
                    {f.label}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {detail.rows.slice(0, 10).map((row) => (
              <tr key={row.id} className="border-t border-slate-100">
                <td className="px-3 py-2 tabular-nums text-slate-500">{row.rowNumber}</td>
                <td className="px-3 py-2">
                  <Badge
                    tone={
                      row.status === 'pending'
                        ? 'success'
                        : row.status === 'duplicate'
                          ? 'neutral'
                          : 'warning'
                    }
                  >
                    {row.status === 'pending' ? 'Ready' : row.status}
                  </Badge>
                </td>
                {detail.fields
                  .filter((f) => detail.columnMapping[f.field])
                  .map((f) => (
                    <td key={f.field} className="px-3 py-2 text-slate-700">
                      {formatCell(row.normalized?.[f.field])}
                    </td>
                  ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return value.slice(0, 10);
  return String(value);
}

function RollbackForm({
  batch,
  pending,
  onCancel,
  onConfirm,
}: {
  batch: ImportBatchSummary;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}): ReactNode {
  const [reason, setReason] = useState('');

  return (
    <div className="space-y-4">
      <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
        This reverses the {batch.importedCount} record{batch.importedCount === 1 ? '' : 's'}{' '}
        <strong>{batch.filename}</strong> created. Sales are voided rather than deleted, so the
        history and your reason stay on the record.
      </p>
      <Field label="Why are you undoing this?" required>
        {(id) => (
          <input
            id={id}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Uploaded the wrong month"
          />
        )}
      </Field>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="danger"
          disabled={reason.trim().length < 3 || pending}
          onClick={() => onConfirm(reason.trim())}
        >
          {pending ? 'Undoing…' : 'Undo import'}
        </Button>
      </div>
    </div>
  );
}
